// The spike's row checks beyond the schemas, shared by the load in the page and the build-time check
// (tools/content.ts). The mechanism is @rpg/content's; these are the game's rules for its own tables.
import { checkMessage, type RowCheck, type TableFile } from "@rpg/content";
import { checkRecipe, type SoundRecipe } from "@rpg/audio";
import { checkSettingRow } from "@rpg-unit/standard-settings";

const num = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

/** Tuning: a value inside its own range. */
const tuning: RowCheck = (r) => {
  if (!num(r.min) || !num(r.max) || !num(r.value)) return null; // the schema reports the types
  if (r.min > r.max) return `min ${r.min} is above max ${r.max}`;
  if (r.value < r.min || r.value > r.max) return `value ${r.value} is outside its range ${r.min}..${r.max}`;
  return null;
};

/** Settings (the standard rows' and the game's own): each kind has what it needs, and the default fits it
 *  (@rpg-unit/standard-settings' rule). */
const settings: RowCheck = (r) => checkSettingRow(r);

/** Sounds (gate D2.3): an important cue names its caption (decision 0063); the recipe is one @rpg/audio can render. The
 *  same check runs on @rpg-unit/sfx-kits' `sfx` table (the kit cues, cut from this table on 2026-10-20). */
const sounds: RowCheck = (r) => {
  if (r.important === true && !r.caption) return "an important sound needs a caption";
  const issues = r.recipe ? checkRecipe(r.recipe as SoundRecipe) : [];
  return issues.length ? issues.join("; ") : null;
};

/** The row checks by table name. */
export const ROW_CHECKS: Readonly<Record<string, RowCheck>> = { strings: checkMessage, "story-strings": checkMessage, "dialogue-strings": checkMessage, "meta-strings": checkMessage, tuning, settings, "game-settings": settings, sounds, sfx: sounds };

/** Planted defects for the content checks (`?faults=broken-ref`, `?faults=raw-string`; the unit test plants the same). */
export const CONTENT_FAULTS = {
  /** A binding names an action that doesn't exist: the build check must fail and the load must skip the binding. */
  brokenRef: false,
  /** A screen shows a string that bypassed the strings table: the raw-string scan must find it. */
  rawString: false,
};

/** The binding the broken-reference defect breaks, and the missing action it points it at. */
export const BROKEN_REF = { table: "bindings", id: "jump.keyboard", missingAction: "leap" } as const;

/** Plant the broken reference in tables read from disk or bundled (mutates them). */
export function plantBrokenRef(tables: TableFile[]): void {
  const t = tables.find((x) => x.table === BROKEN_REF.table);
  const row = t?.rows.find((r) => (r as { id?: string }).id === BROKEN_REF.id) as { action: { table: string; id: string } } | undefined;
  if (!row) throw new Error(`plantBrokenRef: no row ${BROKEN_REF.table}/${BROKEN_REF.id} to break (the defect did not apply)`);
  row.action = { table: "actions", id: BROKEN_REF.missingAction };
}

/** The raw string the raw-string defect puts on screen. */
export const RAW_STRING = "Press any key";
